import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { CollapsibleSection } from "@/components/ui/CollapsibleSection";
import { Spinner } from "@/components/ui/Spinner";
import { TableSortResetBar, useTableSort } from "@/components/dataTable";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useStatusSichtbarkeit } from "@/hooks/useStatusSichtbarkeit";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import { isElectronWithOpenFolder } from "@/lib/electron";
import {
  fetchHeroProjectSteps,
  getColumnSettings,
  getProjectTableHeroFields,
  listOrdersWithTotal,
  listYears,
  PROJECT_WORKFLOW_STATUS_LABELS,
  PROJECT_WORKFLOW_STATUSES,
  saveColumnSettings,
} from "@/lib/projects";
import type { Order, ProjectColumnSettings, ProjectColumnView } from "@/lib/projects";
import { useRealtime } from "@/lib/useRealtime";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import {
  clampColumnWidth,
  getAvailableColumns,
  getDefaultColumns,
  normalizeColumnSelection,
} from "@/pages/projekte/columns";
import type { HeroCustomField } from "@/pages/projekte/columns";
import { ProjectColumnsDialog } from "@/pages/projekte/ProjectColumnsDialog";
import { ProjectTable } from "@/pages/projekte/ProjectTable";
import { SearchInput } from "@/pages/projekte/SearchInput";
import { useBookedHours } from "@/pages/projekte/useBookedHours";
import { useHoursUsage } from "@/pages/projekte/useHoursUsage";
import { useOnProjectCreated } from "@/pages/projekte/useNewProject";
import type { StatusFilter, StatusFilterValue, YearFilter } from "@/pages/projekte/useProjectFilters";

const PAGE_SIZE = 50;

interface KundeProjektFilters {
  q: string;
  /** `null` = alle Jahre, inklusive S-Ordner. */
  jahr: YearFilter;
  status: StatusFilterValue[];
}

const EMPTY_FILTERS: KundeProjektFilters = {
  q: "",
  jahr: null,
  status: [],
};

const ALL_STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "alle", label: "Alle" },
  ...PROJECT_WORKFLOW_STATUSES.map((value) => ({
    value,
    label: PROJECT_WORKFLOW_STATUS_LABELS[value],
  })),
];

function toggleStatus(current: StatusFilterValue[], value: StatusFilter): StatusFilterValue[] {
  if (value === "alle") return [];
  return current.includes(value) ? current.filter((status) => status !== value) : [...current, value];
}

function hasNarrowingFilters(filters: KundeProjektFilters): boolean {
  return Boolean(filters.q.trim()) || filters.jahr !== null || filters.status.length > 0;
}

function formatProjectCount(count: number): string {
  return count === 1 ? "1 Projekt" : `${count.toLocaleString("de-DE")} Projekte`;
}

interface KundeProjekteSectionProps {
  geschaeftspartnerId: number;
  /** Gesamtzahl ohne Suche/Jahr/Status – für die Badge am Reiter. */
  onTotalCount?: (count: number) => void;
}

/**
 * Projekte eines Kunden: dieselbe Tabelle, Suche und Spaltensortierung wie
 * unter Projekte, fest auf diesen Geschäftspartner eingeschränkt.
 */
export function KundeProjekteSection({ geschaeftspartnerId, onTotalCount }: KundeProjekteSectionProps) {
  const { sort, toggle, reset: resetSort } = useTableSort();
  const { user } = useCurrentUser();
  const { visibleStatuses } = useStatusSichtbarkeit();
  const { open: openProjectOverview } = useProjectOverview();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const showOpenFolderColumn = useMemo(
    () => userIsBuero(user) && isElectronWithOpenFolder(),
    [user],
  );

  const [filters, setFilters] = useState<KundeProjektFilters>(EMPTY_FILTERS);
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [ordersToken, setOrdersToken] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [heroFields, setHeroFields] = useState<HeroCustomField[]>([]);
  const [settings, setSettings] = useState<ProjectColumnSettings>({ desktop: null, mobile: null });
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [years, setYears] = useState<number[]>([]);
  const [yearsLoading, setYearsLoading] = useState(false);

  const isMountedRef = useRef(true);
  const requestSeqRef = useRef(0);
  const listAbortRef = useRef<AbortController | null>(null);
  const onTotalCountRef = useRef(onTotalCount);
  onTotalCountRef.current = onTotalCount;

  useEffect(() => {
    setOrders([]);
    setTotalCount(null);
    setLoading(true);
    setFilters(EMPTY_FILTERS);
    resetSort();
  }, [geschaeftspartnerId, resetSort]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      listAbortRef.current?.abort();
      listAbortRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getProjectTableHeroFields().catch(() => []),
      getColumnSettings().catch(() => ({ desktop: null, mobile: null })),
    ]).then(([fields, columnSettings]) => {
      if (cancelled) return;
      setHeroFields(fields);
      setSettings(columnSettings);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setYearsLoading(true);
    listYears()
      .then((list) => {
        if (cancelled) return;
        const set = new Set(list);
        set.add(new Date().getFullYear());
        setYears([...set].sort((a, b) => b - a));
      })
      .catch(() => {
        if (!cancelled) setYears([new Date().getFullYear()]);
      })
      .finally(() => {
        if (!cancelled) setYearsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const statusOptions = useMemo(() => {
    const visible = new Set(visibleStatuses("projekte"));
    return ALL_STATUS_OPTIONS.filter((opt) => opt.value === "alle" || visible.has(opt.value));
  }, [visibleStatuses]);

  const visibleColumns = useMemo(() => {
    const view = isMobile ? settings.mobile : settings.desktop;
    const available = getAvailableColumns(heroFields);
    let cols = view?.columns?.length
      ? normalizeColumnSelection(view.columns, available)
      : getDefaultColumns(heroFields);
    if (!showOpenFolderColumn) cols = cols.filter((column) => column !== "open");
    return cols;
  }, [isMobile, settings, heroFields, showOpenFolderColumn]);

  const visibleWidths = useMemo<Record<string, number>>(() => {
    const view = isMobile ? settings.mobile : settings.desktop;
    const widths = view?.widths ?? {};
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(widths)) {
      const num = Number(value);
      if (Number.isFinite(num) && num > 0) out[key] = clampColumnWidth(num);
    }
    return out;
  }, [isMobile, settings]);

  const needsTimesheets = visibleColumns.includes("timesheets");
  const narrowed = hasNarrowingFilters(filters);

  const requestParams = useMemo(
    () => ({
      geschaeftspartner_id: geschaeftspartnerId,
      q: filters.q || undefined,
      jahr: filters.jahr === null ? undefined : filters.jahr,
      status: filters.status.length ? filters.status.join(",") : undefined,
      sort: sort?.key,
      direction: sort?.direction,
      include_timesheets: needsTimesheets,
      include_timesheet_numbers: false,
      include_hero_steps: false,
    }),
    [geschaeftspartnerId, filters, sort, needsTimesheets],
  );

  const loadFirstPage = useCallback(async () => {
    const requestId = ++requestSeqRef.current;
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const { orders: list, totalCount: total } = await listOrdersWithTotal(
        { ...requestParams, limit: PAGE_SIZE, offset: 0 },
        { signal: controller.signal },
      );
      if (!isMountedRef.current || requestId !== requestSeqRef.current) return;
      setOrders(list);
      setTotalCount(total);
      setHasMore(list.length >= PAGE_SIZE);
      setOrdersToken((token) => token + 1);
      if (!narrowed && total != null) onTotalCountRef.current?.(total);
    } catch (err) {
      if ((err instanceof DOMException || err instanceof Error) && err.name === "AbortError") return;
      if (!isMountedRef.current || requestId !== requestSeqRef.current) return;
      setError(err instanceof ApiError ? err.message : "Projekte konnten nicht geladen werden.");
      setOrders([]);
      setTotalCount(null);
      setHasMore(false);
    } finally {
      if (isMountedRef.current && requestId === requestSeqRef.current) setLoading(false);
    }
  }, [requestParams, narrowed]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    const requestId = requestSeqRef.current;
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    setLoadingMore(true);
    try {
      const { orders: list } = await listOrdersWithTotal(
        { ...requestParams, limit: PAGE_SIZE, offset: orders.length },
        { signal: controller.signal },
      );
      if (!isMountedRef.current || requestId !== requestSeqRef.current) return;
      setOrders((prev) => [...prev, ...list]);
      setHasMore(list.length >= PAGE_SIZE);
      setOrdersToken((token) => token + 1);
    } catch (err) {
      if ((err instanceof DOMException || err instanceof Error) && err.name === "AbortError") return;
    } finally {
      if (isMountedRef.current && requestId === requestSeqRef.current) setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMore, orders.length, requestParams]);

  useRealtime({
    scopes: ["auftraege:list"],
    domains: ["auftraege", "projects", "hero"],
    events: ["auftraege_changed", "projects_changed", "hero_changed", "auftrag_neu", "hero_custom_fields_updated"],
    onChange: () => {
      void loadFirstPage();
    },
  });

  useOnProjectCreated(() => {
    void loadFirstPage();
  });

  const setQuery = useCallback((q: string) => {
    setFilters((prev) => (prev.q === q ? prev : { ...prev, q }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
  }, []);

  const ordersRef = useRef(orders);
  ordersRef.current = orders;
  const needsHeroKundeFallback = useMemo(
    () =>
      orders.some(
        (order) =>
          order.use_hero_customer_name_in_projects &&
          !(order.hero_kunde && String(order.hero_kunde).trim()),
      ),
    [orders],
  );

  useEffect(() => {
    if (!needsHeroKundeFallback) return;
    const projectIds = Array.from(
      new Set(
        ordersRef.current
          .map((order) => order.hero_project_id)
          .filter((id): id is number => typeof id === "number" && id > 0),
      ),
    );
    if (!projectIds.length) return;
    let cancelled = false;
    void (async () => {
      const stepMap = await fetchHeroProjectSteps(projectIds);
      if (cancelled || !isMountedRef.current || stepMap.size === 0) return;
      setOrders((prev) =>
        prev.map((order) => {
          const projectId = order.hero_project_id;
          if (typeof projectId !== "number") return order;
          const info = stepMap.get(projectId);
          if (!info?.hero_kunde) return order;
          if (order.hero_kunde && String(order.hero_kunde).trim()) return order;
          if (!order.use_hero_customer_name_in_projects) return order;
          return { ...order, hero_kunde: info.hero_kunde };
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [needsHeroKundeFallback, ordersToken]);

  const showHoursUsage = visibleColumns.includes("hours_usage");
  const hoursUsageById = useHoursUsage(orders, showHoursUsage);
  const showBookedHours = visibleColumns.includes("booked_hours");
  const bookedHoursById = useBookedHours(orders, showBookedHours);

  const widthSaveTimerRef = useRef<number | null>(null);
  const pendingSettingsRef = useRef<ProjectColumnSettings | null>(null);
  useEffect(() => {
    return () => {
      if (widthSaveTimerRef.current) window.clearTimeout(widthSaveTimerRef.current);
    };
  }, []);

  const handleColumnWidthChange = useCallback(
    (key: string, width: number) => {
      const clamped = clampColumnWidth(width);
      setSettings((prev) => {
        const view: "desktop" | "mobile" = isMobile ? "mobile" : "desktop";
        const current: ProjectColumnView = prev[view] ?? { columns: [], widths: {} };
        const nextSettings: ProjectColumnSettings = {
          ...prev,
          [view]: {
            columns: current.columns ?? [],
            widths: { ...(current.widths ?? {}), [key]: clamped },
          },
        };
        pendingSettingsRef.current = nextSettings;
        return nextSettings;
      });
      if (widthSaveTimerRef.current) window.clearTimeout(widthSaveTimerRef.current);
      widthSaveTimerRef.current = window.setTimeout(() => {
        const payload = pendingSettingsRef.current;
        pendingSettingsRef.current = null;
        widthSaveTimerRef.current = null;
        if (!payload) return;
        saveColumnSettings(payload).catch(() => {
          /* Lokale Breite bleibt stehen, auch wenn das Speichern fehlschlägt. */
        });
      }, 500);
    },
    [isMobile],
  );

  async function handleSaveColumnSettings(next: ProjectColumnSettings) {
    try {
      const saved = await saveColumnSettings(next);
      if (isMountedRef.current) setSettings(saved);
    } catch {
      if (isMountedRef.current) setSettings(next);
    }
  }

  const countLabel =
    loading && orders.length === 0 ? "Lade …" : formatProjectCount(totalCount ?? orders.length);

  return (
    <div className="space-y-4">
      <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 md:px-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-baseline gap-x-2 text-lg font-semibold tracking-tight text-label">
              <span>Projekte</span>
              <span className="text-sm font-medium tabular-nums text-label-secondary" aria-live="polite">
                · {countLabel}
              </span>
            </h2>
            <p className="mt-0.5 text-sm text-label-secondary">
              Alle Projekte dieses Kunden. Suche und Sortierung wie in der Projektliste.
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
            <SearchInput
              value={filters.q}
              onChange={setQuery}
              debounceMs={300}
              placeholder="Projekte durchsuchen: Nummer, Angebot, Ort …"
              loading={loading && Boolean(filters.q.trim())}
              className="w-full min-w-0 sm:min-w-[16rem] sm:flex-1"
            />
            <Button
              type="button"
              variant="secondary"
              className="shrink-0 whitespace-nowrap"
              onClick={() => setColumnsDialogOpen(true)}
            >
              Spalten
            </Button>
          </div>
        </div>
      </section>

      <CollapsibleSection
        sectionKey="kunde.projekte.filter"
        title="Filter"
        summary={narrowed ? "Filter aktiv" : "Alle Jahre"}
        actions={
          narrowed ? (
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-lg px-2 py-1 text-xs font-medium text-accent transition hover:bg-fill-tertiary"
            >
              Zurücksetzen
            </button>
          ) : null
        }
      >
        <div className="space-y-3 rounded-ios-lg bg-fill-quaternary/20 px-4 py-3 ring-1 ring-separator/30 dark:ring-separator/45">
          <div>
            <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
              Jahr
              {yearsLoading ? <Spinner className="h-3.5 w-3.5" /> : null}
            </div>
            <div
              className="-mx-0.5 flex gap-2 overflow-x-auto px-0.5 pb-1 [scrollbar-width:thin]"
              role="toolbar"
              aria-label="Jahr filtern"
            >
              <FilterChip active={filters.jahr === null} onClick={() => setFilters((prev) => ({ ...prev, jahr: null }))}>
                Alle Jahre
              </FilterChip>
              {years.map((year) => (
                <FilterChip
                  key={year}
                  active={filters.jahr === year}
                  onClick={() => setFilters((prev) => ({ ...prev, jahr: year }))}
                >
                  {year}
                </FilterChip>
              ))}
              <FilterChip
                active={filters.jahr === "S"}
                onClick={() => setFilters((prev) => ({ ...prev, jahr: "S" }))}
              >
                S-Ordner
              </FilterChip>
            </div>
          </div>
          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-label-tertiary">Status</div>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Status filtern">
              {statusOptions.map((opt) => {
                const active =
                  opt.value === "alle" ? filters.status.length === 0 : filters.status.includes(opt.value);
                return (
                  <FilterChip
                    key={opt.value}
                    active={active}
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, status: toggleStatus(prev.status, opt.value) }))
                    }
                  >
                    {opt.label}
                  </FilterChip>
                );
              })}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {error ? (
        <Alert variant="error" title="Liste konnte nicht geladen werden">
          {error}
        </Alert>
      ) : null}

      <section>
        {loading && orders.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-label-secondary">
            <Spinner />
            <span className="ml-2 text-sm">Projekte werden geladen …</span>
          </div>
        ) : (
          <>
            <TableSortResetBar visible={sort != null} onReset={resetSort} />
            <ProjectTable
              rows={orders}
              columns={visibleColumns}
              heroFields={heroFields}
              widths={visibleWidths}
              highlightQuery={filters.q}
              hoursUsageById={hoursUsageById}
              bookedHoursById={bookedHoursById}
              loading={loading}
              loadingMore={loadingMore}
              hasMore={hasMore}
              onLoadMore={loadMore}
              onRowOpen={(row) => openProjectOverview(row.id)}
              onColumnWidthChange={handleColumnWidthChange}
              sort={sort}
              onSort={toggle}
              emptyText={
                narrowed
                  ? "Keine Projekte für diese Suche gefunden."
                  : "Für diesen Kunden sind noch keine Projekte hinterlegt."
              }
              onHeroCreated={(orderId, heroProjectId) => {
                setOrders((prev) =>
                  prev.map((order) =>
                    order.id === orderId ? { ...order, hero_project_id: heroProjectId } : order,
                  ),
                );
                setOrdersToken((token) => token + 1);
              }}
            />
            {!loading && orders.length === 0 && !error && narrowed ? (
              <div className="mt-4 flex flex-col items-center gap-3 rounded-ios bg-fill-quaternary/25 px-4 py-4 text-center ring-1 ring-separator/30">
                <p className="max-w-md text-sm text-label-secondary">
                  Keine Projekte gefunden. Filter zurücksetzen, um alle Projekte dieses Kunden zu sehen.
                </p>
                <Button type="button" variant="secondary" onClick={resetFilters}>
                  Filter zurücksetzen
                </Button>
              </div>
            ) : null}
          </>
        )}
      </section>

      <ProjectColumnsDialog
        open={columnsDialogOpen}
        onClose={() => setColumnsDialogOpen(false)}
        heroFields={heroFields}
        settings={settings}
        onSave={handleSaveColumnSettings}
        showOpenFolderColumn={showOpenFolderColumn}
      />
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition",
        active ? "bg-accent text-label-on-accent shadow-sm" : "bg-fill-tertiary text-label hover:bg-fill",
      )}
    >
      {children}
    </button>
  );
}
